import React, { useState, useEffect, useMemo, ChangeEvent } from 'react';
import { Modal, Form, InputGroup, Alert } from 'react-bootstrap';
import Badge from 'components/base/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRefresh,
  faTrash,
  faCheckCircle,
  faTimesCircle,
  faClock,
  faExclamationTriangle,
  faFilter
} from '@fortawesome/free-solid-svg-icons';
import {
  faCalendarPlus,
  faCalendarCheck
} from '@fortawesome/free-regular-svg-icons';
import { UilCloudUpload } from '@iconscout/react-unicons';
import { ColumnDef, Row as TanstackRow } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import RevealDropdown from 'components/base/RevealDropdown';
import Button from 'components/base/Button';
import SearchBox from 'components/common/SearchBox';
import HVASpinner from 'components/base/HVASpinner';
import HVAToast from 'components/common/HVAToast';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { Dropdown } from 'react-bootstrap';
import {
  fetchCollections,
  fetchAllDocuments,
  deleteDocument,
  KnowledgeHubCollection,
  KnowledgeHubDocument
} from '../../services/knowledgehub.service';
import DocumentUploader from './DocumentUploader';
import docsimage from 'assets/img/spot-illustrations/docs2.png';
import docsimagedark from 'assets/img/spot-illustrations/docs2dark.png';

interface DocumentsManagerProps {
  onUpdate?: () => void;
}

// Componente para las acciones de cada fila
const DocumentActionCell = ({
  document,
  onDelete
}: {
  document: KnowledgeHubDocument;
  onDelete: (id: string, name: string) => void;
}) => {
  return (
    <RevealDropdown
      btnClassName="p-2 pt-1 pb-1 fs-11"
      aria-label="Document actions menu"
    >
      <Dropdown.Item onClick={() => onDelete(document.id, document.name)}>
        <FontAwesomeIcon
          icon={faTrash}
          className="fs-8 p-2 pt-0 pb-0 text-danger"
        />
        Delete Document
      </Dropdown.Item>
    </RevealDropdown>
  );
};

const DocumentsManager: React.FC<DocumentsManagerProps> = ({ onUpdate }) => {
  const [documents, setDocuments] = useState<KnowledgeHubDocument[]>([]);
  const [collections, setCollections] = useState<KnowledgeHubCollection[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Toast states
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<
    'success' | 'error' | 'warning' | 'info'
  >('info');

  const [showUploadModal, setShowUploadModal] = useState(false);

  // Helper function to show toast
  const showToastMessage = (
    message: string,
    variant: 'success' | 'error' | 'warning' | 'info'
  ) => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  useEffect(() => {
    loadCollections();
  }, []);

  useEffect(() => {
    if (collections.length > 0 || selectedCollection === 'all') {
      loadDocuments();
    }
  }, [selectedCollection, collections]);

  const loadCollections = async () => {
    try {
      setLoading(true);
      const data = await fetchCollections();
      setCollections(data);
    } catch (err) {
      showToastMessage('Error loading collections', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    try {
      setLoadingDocuments(true);

      // Obtener TODOS los documentos de una vez
      const allDocs = await fetchAllDocuments();

      // Filtrar por colección si es necesario
      let filteredDocs = allDocs;
      if (selectedCollection !== 'all') {
        filteredDocs = allDocs.filter(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          doc => (doc as any).collectionId === selectedCollection
        );
      }

      setDocuments(filteredDocs);
    } catch (err) {
      showToastMessage('Error loading documents', 'error');
      console.error(err);
      setDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleDeleteClick = (id: string, name: string) => {
    setDocumentToDelete({ id, name });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    try {
      setDeleting(true);
      await deleteDocument(documentToDelete.id);
      setShowDeleteModal(false);
      setDocumentToDelete(null);
      await loadDocuments();
      showToastMessage('Document deleted successfully', 'success');
      if (onUpdate) onUpdate();
    } catch (err) {
      showToastMessage('Error deleting document', 'error');
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'indexed':
        return (
          <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
        );
      case 'error':
        return <FontAwesomeIcon icon={faTimesCircle} className="text-danger" />;
      case 'processing':
        return <FontAwesomeIcon icon={faClock} className="text-warning" />;
      case 'pending':
        return <FontAwesomeIcon icon={faClock} className="text-info" />;
      default:
        return (
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-secondary"
          />
        );
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      indexed: 'success',
      error: 'danger',
      processing: 'warning',
      pending: 'info'
    } as const;

    const bgColor = variants[status as keyof typeof variants] || 'secondary';

    return (
      <Badge
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        bg={bgColor as any}
        variant="phoenix"
      >
        <span className="me-1">{getStatusIcon(status)}</span>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  // Definir columnas de la tabla
  const columns = useMemo<ColumnDef<KnowledgeHubDocument>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Document Name',
        cell: ({ row }: { row: TanstackRow<KnowledgeHubDocument> }) => (
          <div className="d-flex align-items-center">
            <strong className="text-body-emphasis">{row.original.name}</strong>
          </div>
        ),
        meta: {
          headerProps: { style: { width: '20%' }, className: 'fs-10 px-1' },
          cellProps: { className: 'py-2 px-1' }
        }
      },
      {
        accessorKey: 'action',
        header: '',
        enableSorting: false,
        cell: ({ row }: { row: TanstackRow<KnowledgeHubDocument> }) => (
          <DocumentActionCell
            document={row.original}
            onDelete={handleDeleteClick}
          />
        ),
        meta: {
          headerProps: { style: { width: '2%' } },
          cellProps: { className: 'py-2' }
        }
      },
      {
        accessorKey: 'collectionName',
        header: 'Collection',
        cell: ({ row }: { row: TanstackRow<KnowledgeHubDocument> }) => (
          <div className="d-flex align-items-center">
            <span className="text-body-emphasis">
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {(row.original as any).collectionName || 'Unknown'}
            </span>
          </div>
        ),
        meta: {
          headerProps: { style: { width: '20%' }, className: 'fs-10' },
          cellProps: { className: 'py-2' }
        }
      },
      {
        accessorKey: 'size',
        header: 'Size',
        cell: ({ row }: { row: TanstackRow<KnowledgeHubDocument> }) => (
          <span className="text-body-tertiary">
            {formatFileSize(row.original.size)}
          </span>
        ),
        meta: {
          headerProps: {
            style: { width: '10%' },
            className: 'fs-10 text-center'
          },
          cellProps: { className: 'py-2 text-center' }
        }
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }: { row: TanstackRow<KnowledgeHubDocument> }) =>
          getStatusBadge(row.original.status),
        meta: {
          headerProps: {
            style: { width: '10%' },
            className: 'fs-10 text-center'
          },
          cellProps: { className: 'py-2 text-center' }
        }
      },
      {
        accessorKey: 'chunksCount',
        header: 'Chunks',
        cell: ({ row }: { row: TanstackRow<KnowledgeHubDocument> }) => (
          <Badge bg="secondary" variant="phoenix">
            {row.original.chunksCount || 0}
          </Badge>
        ),
        meta: {
          headerProps: {
            style: { width: '10%' },
            className: 'fs-10 text-center'
          },
          cellProps: { className: 'py-2 text-center' }
        }
      },
      {
        accessorKey: 'createdAt',
        header: 'Uploaded',
        cell: ({ row }: { row: TanstackRow<KnowledgeHubDocument> }) => (
          <span className="text-body-tertiary">
            <FontAwesomeIcon icon={faCalendarPlus} className="me-1" />
            {formatDate(row.original.createdAt)}
          </span>
        ),
        meta: {
          headerProps: { style: { width: '14%' }, className: 'fs-10' },
          cellProps: { className: 'py-2' }
        }
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated',
        cell: ({ row }: { row: TanstackRow<KnowledgeHubDocument> }) => (
          <span className="text-body-tertiary">
            <FontAwesomeIcon icon={faCalendarCheck} className="me-1" />
            {formatDate(row.original.updatedAt)}
          </span>
        ),
        meta: {
          headerProps: { style: { width: '14%' }, className: 'fs-10' },
          cellProps: { className: 'py-2' }
        }
      }
    ],
    []
  );

  const table = useAdvanceTable({
    data: documents,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  return (
    <>
      {/* Toast Notification */}
      <HVAToast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
      />

      {/* Header */}
      <div className="mb-3 pt-3">
        <div className="d-flex flex-wrap gap-3">
          <div className="d-flex gap-2 align-items-center">
            {/* Collection Filter */}
            <InputGroup style={{ width: '250px' }}>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faFilter} />
              </InputGroup.Text>
              <Form.Select
                value={selectedCollection}
                onChange={e => setSelectedCollection(e.target.value)}
                disabled={loading || collections.length === 0}
              >
                <option value="all">All Collections</option>
                {collections.map(col => (
                  <option key={col.id} value={col.id}>
                    {col.name}
                  </option>
                ))}
              </Form.Select>
            </InputGroup>
            <SearchBox
              placeholder="Search documents..."
              onChange={handleSearchInputChange}
            />
          </div>

          <div className="ms-auto">
            <Button
              variant="link"
              onClick={loadDocuments}
              disabled={loadingDocuments}
            >
              <FontAwesomeIcon icon={faRefresh} className="me-2" />
              Refresh
            </Button>
            <Button variant="primary" onClick={() => setShowUploadModal(true)}>
              <UilCloudUpload size={18} className="me-2" />
              Upload Documents
            </Button>
          </div>
        </div>
      </div>

      {/* Documents Table */}
      {loading || loadingDocuments ? (
        <HVASpinner />
      ) : documents.length === 0 ? (
        <div className="text-center py-5">
          <img
            src={docsimage}
            alt={`Supported formats`}
            width="190px"
            className="d-dark-none pe-1"
          />
          <img
            src={docsimagedark}
            alt={`Supported formats`}
            width="190px"
            className="d-light-none pe-1"
          />
          <p className="text-body-tertiary fs-9">
            {selectedCollection === 'all'
              ? 'No documents found in any collection.'
              : 'No documents found in this collection.'}
            <br />
            Upload some documents to get started!
          </p>
        </div>
      ) : (
        <AdvanceTableProvider {...table}>
          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-translucent position-relative top-1">
            <AdvanceTable
              tableProps={{
                size: 'sm',
                className: 'phoenix-table fs-9'
              }}
              rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
            />
            <AdvanceTableFooter pagination />
          </div>
        </AdvanceTableProvider>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        size="lg"
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Document</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-6">
          <p className="mb-0">
            Are you sure you want to delete the document{' '}
            <strong>{documentToDelete?.name}</strong>?
          </p>
          <Alert variant="subtle-warning" className="mt-3 mb-0">
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            This action cannot be undone. All vectors and chunks associated with
            this document will be permanently deleted.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete Document'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Upload Documents Modal */}
      <Modal
        show={showUploadModal}
        size="xl"
        onHide={() => setShowUploadModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Upload Documents to Knowledge Hub</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-6 pt-4 pb-4">
          <DocumentUploader
            onUpdate={() => {
              loadDocuments(); // Recargar documentos
              if (onUpdate) onUpdate(); // Actualizar stats si existe
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUploadModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DocumentsManager;
